import React from "react"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { RegistrationButtonFooter } from "./RegistrationButtons"
import { Button, Typography } from "@carrotfertility/carotene-core"
import { RegistrationFlowTitle } from "./RegistrationFlowTitle"
import { CarrotPlansPaths, Paths } from "#/utils/Paths"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"

interface PartnerRegistrationCompleteProps {
  isInitialCarrotPlanRoutingCompleted: boolean
  employeeId: number
}

function continueToRouting(): void {
  window.location.href = CarrotPlansPaths.ENROLLMENT
}

function goToHomePage(): void {
  window.location.href = Paths.HOME
}

const headerText = defineMessage({ defaultMessage: "Thanks." })
const descriptionText = defineMessage({
  defaultMessage:
    "You can now explore Carrot or tell us a little more about yourself to get a personalized Carrot Plan and unlock your full benefit."
})
const calibratedEngagementDescriptionText = defineMessage({
  defaultMessage: "You can now explore your Carrot benefit."
})

function RegistrationDescription({
  isCalibratedEngagementEnabled
}: {
  isCalibratedEngagementEnabled: boolean
}): JSX.Element {
  const intl = useIntl()

  return (
    <Typography>
      {intl.formatMessage(isCalibratedEngagementEnabled ? calibratedEngagementDescriptionText : descriptionText)}
    </Typography>
  )
}

function StandardButtonFooter({
  isCalibratedEngagementEnabled
}: {
  isCalibratedEngagementEnabled: boolean
}): JSX.Element {
  return (
    <RegistrationButtonFooter
      exploreBenefitButton={
        <Button onClick={goToHomePage} color="secondary" variant="outlined">
          <FormattedMessage defaultMessage="Explore your benefit" />
        </Button>
      }
      getCarrotPlanButton={
        isCalibratedEngagementEnabled ? null : (
          <Button onClick={continueToRouting} color="primary" variant="contained">
            <FormattedMessage defaultMessage="Get your Carrot Plan" />
          </Button>
        )
      }
    />
  )
}

function RegistrationCompleteLayout({
  employeeId,
  isCalibratedEngagementEnabled,
  interactiveElements
}: {
  employeeId: number
  isCalibratedEngagementEnabled: boolean
  interactiveElements: JSX.Element
}): JSX.Element {
  const intl = useIntl()

  return (
    <>
      <RegistrationFlowTitle title={intl.formatMessage(headerText)} />
      <RegistrationFlowLayout
        header={intl.formatMessage(headerText)}
        imageName="illo-underwater-dashboard"
        description={<RegistrationDescription isCalibratedEngagementEnabled={isCalibratedEngagementEnabled} />}
        showLocaleSelect={false}
        interactiveElements={interactiveElements}
        employeeId={employeeId}
      />
    </>
  )
}

export function RegistrationComplete({ employeeId }: { employeeId: number }): JSX.Element {
  const calibratedEngagement = useCalibratedEngagementEnabled()

  if (calibratedEngagement.isLoading) {
    return <></>
  }

  return (
    <RegistrationCompleteLayout
      employeeId={employeeId}
      isCalibratedEngagementEnabled={calibratedEngagement.data}
      interactiveElements={<StandardButtonFooter isCalibratedEngagementEnabled={calibratedEngagement.data} />}
    />
  )
}

export function PartnerRegistrationComplete({
  isInitialCarrotPlanRoutingCompleted,
  employeeId
}: PartnerRegistrationCompleteProps): JSX.Element {
  const calibratedEngagement = useCalibratedEngagementEnabled()

  if (calibratedEngagement.isLoading) {
    return <></>
  }

  const interactiveElements = isInitialCarrotPlanRoutingCompleted ? (
    <RegistrationButtonFooter
      getCarrotPlanButton={
        <Button onClick={goToHomePage} color="primary" variant="contained">
          <FormattedMessage defaultMessage="Explore your benefit" />
        </Button>
      }
    />
  ) : (
    <StandardButtonFooter isCalibratedEngagementEnabled={calibratedEngagement.data} />
  )

  return (
    <RegistrationCompleteLayout
      employeeId={employeeId}
      isCalibratedEngagementEnabled={calibratedEngagement.data}
      interactiveElements={interactiveElements}
    />
  )
}
